/**
 * Profile push — upsert the current user's profile row in public.profiles.
 *
 * Called directly (no outbox) because there is at most one profile row per
 * user, and idempotent upserts are cheap. Failures are non-fatal — the
 * caller should log but not break the UI.
 */

import { supabase, SupabaseNotConfiguredError } from "../supabase";

export async function pushProfile(
  userId: string,
  role: "student" | "tutor",
  displayName?: string,
): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase.from("profiles").upsert(
    {
      user_id: userId,
      role,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      display_name: displayName ?? null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id" },
  );
  if (error) throw error;
}

/**
 * Settings toggle for the bot's opt-in "word of the day" push (see
 * supabase/functions/word-of-day). An UPDATE, not an upsert: the profile row
 * always already exists by the time this is reachable (telegram-auth/host-auth
 * create it at sign-in), and only this one column is touched.
 */
export async function pushWordOfDayOptIn(userId: string, optIn: boolean): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { data, error } = await supabase
    .from("profiles")
    .update({ word_of_day_opt_in: optIn })
    .eq("user_id", userId).select("word_of_day_opt_in").single();
  if (error) throw error;
  if (!data || data.word_of_day_opt_in !== optIn) throw new Error("Preference was not saved");
}

/** Current opt-in value for the signed-in account, so Settings reflects it on a new device. */
export async function pullWordOfDayOptIn(userId: string): Promise<boolean> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { data, error } = await supabase
    .from("profiles")
    .select("word_of_day_opt_in")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data?.word_of_day_opt_in ?? false;
}
